import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { User, ShoppingCart, Menu, Search, MapPin } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import Sidebar from './Sidebar';
import CartSidebar from './CartSidebar';
import DishImageSplash from './common/DishImageSplash';
import LanguageSwitcher from './LanguageSwitcher';
import AddressPopup from './AddressPopup';
import API_BASE_URL from '../config/api';
import { getImageUrl, resolveDishImagePath, resolveRestaurantImagePath } from '../services/api';
import namkeLogoHeader from '../assets/namke-logo-header.png';

const ResultImage = ({ src, alt, fallbackEmoji }) => {
  const [error, setError] = useState(false);
  if (!src || error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <span className="text-2xl">{fallbackEmoji}</span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-cover"
      onError={() => setError(true)}
    />
  );
};

export default function NavBar({ onSearchResults }) {
    const { t } = useTranslation();
    const { cartItems } = useCart();
    const cartCount = cartItems.length;
    const [isSticky, setIsSticky] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('access_token'));
    const [savedAddress, setSavedAddress] = useState(() => {
      try {
        return JSON.parse(localStorage.getItem('namke_user_address') || 'null');
      } catch {
        return null;
      }
    });
    const [showAddressPopup, setShowAddressPopup] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showNotificationMenu, setShowNotificationMenu] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const searchRef = useRef(null);
    const searchInputRef = useRef(null);
    const userMenuRef = useRef(null);
    const notificationRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
      const handleScroll = () => {
        setIsSticky(window.scrollY > 50);
      };
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }, []);


    // Auth state sync (in case of login/logout in another tab)
    useEffect(() => {
      const handleStorage = () => setIsAuthenticated(!!localStorage.getItem('access_token'));
      window.addEventListener('storage', handleStorage);
      return () => window.removeEventListener('storage', handleStorage);
    }, []);

    // Load notifications for authenticated users
    useEffect(() => {
      const loadNotifications = async () => {
        if (!isAuthenticated) {
          setNotifications([]);
          setUnreadCount(0);
          return;
        }
        try {
          const token = localStorage.getItem('access_token');
          if (!token) {
            setNotifications([]);
            setUnreadCount(0);
            return;
          }
          const headers = {
            'Authorization': `Bearer ${token}`,
          };
          const [listRes, unreadRes] = await Promise.all([
            fetch(`${API_BASE_URL}/notifications?limit=20`, { headers }),
            fetch(`${API_BASE_URL}/notifications/unread-count`, { headers }),
          ]);

          if (!listRes.ok) {
            throw new Error('Erreur lors du chargement des notifications');
          }
          const notifData = await listRes.json();
          const notificationsArray = Array.isArray(notifData) ? notifData : [];
          setNotifications(notificationsArray);

          if (unreadRes.ok) {
            const unreadJson = await unreadRes.json();
            setUnreadCount(Number(unreadJson?.unread_count || 0));
          } else {
            setUnreadCount(
              notificationsArray.filter((n) => !n.is_read && !n.read).length
            );
          }
        } catch (e) {
          console.error('Erreur lors du chargement des notifications:', e);
          setNotifications([]);
          setUnreadCount(0);
        }
      };
      loadNotifications();
    }, [isAuthenticated]);

    // Close user menu on click outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
          setShowUserMenu(false);
        }
        if (searchRef.current && !searchRef.current.contains(event.target)) {
          setShowSuggestions(false);
          setIsSearchExpanded(false);
        }
        if (notificationRef.current && !notificationRef.current.contains(event.target)) {
          setShowNotificationMenu(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    // Autofocus l'input quand la barre de recherche se déplie
    useEffect(() => {
      if (isSearchExpanded && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [isSearchExpanded]);

    // Debounce search
    useEffect(() => {
      const timeoutId = setTimeout(() => {
        if (searchQuery.length >= 2) {
          performSearch();
        }
      }, 300);
      return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    const performSearch = async () => {
      if (searchQuery.length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      setIsSearching(true);
      setShowSuggestions(true);
      try {
        // Fetch restaurants
        const restaurantsResponse = await fetch(`${API_BASE_URL}/restaurants/search?q=${encodeURIComponent(searchQuery)}`);
        const restaurantsData = await restaurantsResponse.json();
        // Fetch dishes
        const dishesResponse = await fetch(`${API_BASE_URL}/dishes/search?q=${encodeURIComponent(searchQuery)}`);
        const dishesData = await dishesResponse.json();
        const allSuggestions = [
          ...restaurantsData.map(restaurant => ({
            id: restaurant.id,
            name: String(restaurant.name || ''),
            image: getImageUrl(resolveRestaurantImagePath(restaurant)),
            type: 'restaurant',
            description: String(restaurant.description || 'Restaurant')
          })),
          ...dishesData.map(dish => ({
            id: dish.id,
            name: String(dish.name || ''),
            image: getImageUrl(resolveDishImagePath(dish)),
            category: dish.category,
            type: 'dish',
            description: String(dish.description || 'Plat')
          }))
        ];
        setSuggestions(allSuggestions.slice(0, 5)); // 5 suggestions max
        setSelectedIndex(-1);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      } finally {
        setIsSearching(false);
      }
    };

    const handleSuggestionClick = (suggestion) => {
      setSearchQuery('');
      setShowSuggestions(false);
      setSelectedIndex(-1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Navigate to the appropriate page based on type
      if (suggestion.type === 'restaurant') {
        navigate(`/restaurant/${suggestion.id}`);
      } else {
        navigate(`/product/${suggestion.id}`);
      }
    };

    const handleSearchKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        setSelectedIndex(-1);
        return;
      }
      if (!showSuggestions || suggestions.length === 0) return;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case 'Enter':
          if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
            e.preventDefault();
            handleSuggestionClick(suggestions[selectedIndex]);
          }
          break;
        default:
          break;
      }
    };

    const handleSearchSubmit = (e) => {
      e.preventDefault();
      if (searchQuery.trim()) {
        navigate(`/?q=${encodeURIComponent(searchQuery.trim())}`);
        setShowSuggestions(false);
      }
    };

    const handleSearch = (e) => {
      const query = e.target.value;
      setSearchQuery(query);
    };

    const handleSearchFocus = () => {
      if (window.innerWidth < 768 && !savedAddress) {
        searchInputRef.current?.blur();
        setShowAddressPopup(true);
        return;
      }
      setIsSearchExpanded(true);
      setShowSuggestions(true);
    };

    const handleAddressSelect = (address) => {
      setSavedAddress(address);
      localStorage.setItem('namke_user_address', JSON.stringify(address));
      setShowAddressPopup(false);
    };

    const handleLogout = () => {
      localStorage.removeItem('access_token');
      setIsAuthenticated(false);
      setShowUserMenu(false);
      navigate('/');
    };

    const handleNotificationClick = (notificationId) => {
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    };

    const markAllAsRead = () => {
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    };

    const formatTimestamp = (timestamp) => {
      const now = new Date();
      const diff = now - timestamp;
      const minutes = Math.floor(diff / (1000 * 60));
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));

      if (minutes < 60) return t('common.minutesAgo', { count: minutes });
      if (hours < 24) return t('common.hoursAgo', { count: hours });
      return t('common.daysAgo', { count: days });
    };

    return (
      <>
      <div
        className="w-full z-50 top-0 py-2 md:py-0 flex items-center gap-2 sm:gap-3 px-2 sm:px-6 h-auto md:h-20 fixed bg-namke-white/95 backdrop-blur-sm border-b border-namke-darkGreen/10"
      >
        {/* Burger Menu et Logo */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-2.5 hover:bg-primary-50 rounded-full transition-all duration-300 active:scale-95"
          >
            <Menu size={22} className="text-namke-black" />
          </button>
          <Link to="/" className="hidden md:flex items-center gap-2">
            <img src={namkeLogoHeader} alt="namke" className="h-9 md:h-12 w-auto object-contain" />
          </Link>
        </div>

        {/* Liens de navigation */}
        <nav className="hidden xl:flex items-center gap-4 shrink-0 text-sm font-medium">
          <NavLink
            to="/"
            end
            className={({ isActive }) => `whitespace-nowrap transition-colors ${isActive ? 'text-namke-darkGreen font-semibold' : 'text-namke-black/80 hover:text-namke-terracotta'}`}
          >
            {t('nav.home', 'Accueil')}
          </NavLink>
          <NavLink
            to="/restaurants"
            className={({ isActive }) => `whitespace-nowrap transition-colors ${isActive ? 'text-namke-darkGreen font-semibold' : 'text-namke-black/80 hover:text-namke-terracotta'}`}
          >
            {t('nav.restaurants', 'Restaurants')}
          </NavLink>
          <NavLink
            to="/traiteur"
            className={({ isActive }) => `whitespace-nowrap transition-colors ${isActive ? 'text-namke-darkGreen font-semibold' : 'text-namke-black/80 hover:text-namke-terracotta'}`}
          >
            {t('nav.cateringService', 'Services traiteurs')}
          </NavLink>
          <NavLink
            to="/store"
            className={({ isActive }) => `whitespace-nowrap transition-colors ${isActive ? 'text-namke-darkGreen font-semibold' : 'text-namke-black/80 hover:text-namke-terracotta'}`}
          >
            {t('nav.groceryShop', 'Grocery shop')}
          </NavLink>
          <NavLink
            to="/about-us"
            className={({ isActive }) => `whitespace-nowrap transition-colors ${isActive ? 'text-namke-darkGreen font-semibold' : 'text-namke-black/80 hover:text-namke-terracotta'}`}
          >
            {t('nav.aboutUs', 'À propos')}
          </NavLink>
        </nav>

        {/* Barre de recherche — pill grise, s'adapte à l'espace disponible */}
        <div
          className="relative flex-1 min-w-0 md:max-w-xl xl:max-w-2xl"
          ref={searchRef}
        >
          <form
            onSubmit={handleSearchSubmit}
            className="flex items-center h-10 md:h-11 w-full bg-namke-cream hover:bg-primary-50 focus-within:bg-namke-white focus-within:ring-1 focus-within:ring-namke-darkGreen/30 transition-colors overflow-hidden rounded-full px-3 sm:px-4 gap-1.5 sm:gap-2"
          >
            <button
              type="submit"
              title={t('common.search')}
              className="shrink-0 flex items-center justify-center text-namke-darkGreen"
            >
              <Search className="w-4 h-4 md:w-5 md:h-5" />
            </button>
            {savedAddress && (
              <button
                type="button"
                onClick={() => setShowAddressPopup(true)}
                className="shrink-0 flex items-center gap-1 md:hidden text-namke-darkGreen border-r border-namke-darkGreen/15 pr-2 mr-0.5"
                title="Changer d'adresse"
              >
                <MapPin className="w-3.5 h-3.5 shrink-0" />
                <span className="text-xs font-medium max-w-[86px] truncate">
                  {savedAddress.display_name?.split(',')[0]}
                </span>
              </button>
            )}
            <input
              ref={searchInputRef}
              type="text"
              placeholder={t('common.searchPlaceholder')}
              value={searchQuery}
              onChange={handleSearch}
              onFocus={handleSearchFocus}
              onKeyDown={handleSearchKeyDown}
              className="flex-1 min-w-0 bg-transparent text-namke-black placeholder-namke-black/45 focus:outline-none text-sm sm:text-base"
            />
          </form>
          {/* Dropdown des suggestions */}
          {isSearchExpanded && showSuggestions && (searchQuery.length >= 2 || suggestions.length > 0) && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-namke-white border border-namke-darkGreen/15 rounded-2xl shadow-lg z-[10003] max-h-80 overflow-y-auto w-full">
              {isSearching ? (
                <div className="p-4 text-center text-namke-black/50">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-namke-olive mx-auto mb-2"></div>
                  {t('common.searching')}
                </div>
              ) : suggestions.length > 0 ? (
                <div>
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={`${suggestion.type}-${suggestion.id}`}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className={`w-full flex items-center gap-3 p-3 hover:bg-primary-50 text-left border-b border-namke-darkGreen/10 last:border-b-0 transition-colors ${
                        index === selectedIndex ? 'bg-primary-50' : ''
                      }`}
                    >
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-namke-cream flex-shrink-0">
                        {suggestion.type === 'dish' ? (
                          <DishImageSplash
                            src={suggestion.image}
                            alt={suggestion.name}
                            dishName={suggestion.name}
                            category={suggestion.category}
                            className="w-full h-full"
                            showLoader={false}
                          />
                        ) : (
                          <ResultImage src={suggestion.image} alt={suggestion.name} fallbackEmoji="🏪" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-namke-black truncate">{suggestion.name}</div>
                        <div className="text-sm text-namke-black/50 flex items-center gap-2">
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-namke-cream text-namke-darkGreen">
                            {suggestion.type === 'restaurant' ? t('common.restaurant') : t('common.dish')}
                          </span>
                          <span className="truncate">{suggestion.description}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                  <div className="p-3 border-t border-namke-darkGreen/10">
                    <button
                      onClick={handleSearchSubmit}
                      className="w-full text-center text-namke-darkGreen hover:text-namke-black font-medium text-sm"
                    >
                      {t('common.seeAllResultsFor', { query: searchQuery })}
                    </button>
                  </div>
                </div>
              ) : searchQuery.length >= 2 ? (
                <div className="p-4 text-center text-namke-black/50">
                  {t('common.noResultsFor', { query: searchQuery })}
                </div>
              ) : null}
            </div>
          )}
        </div>
        {/* Panier + actions à droite */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Icône panier — visible uniquement en responsive (remplacée par la version desktop ci-dessous sur md+) */}
          <button
            type="button"
            onClick={() => setIsCartOpen(true)}
            className="relative p-2 hover:bg-primary-50 rounded-full transition-colors md:hidden"
          >
            <ShoppingCart size={22} className="text-namke-black/80" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-namke-terracotta text-namke-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </button>
          {/* Actions à droite (cachées sur mobile, visibles sur md+) */}
          <div className="hidden md:flex items-center gap-4">
          <LanguageSwitcher />
          {/* Icône panier */}
          <button
            type="button"
            onClick={() => setIsCartOpen(true)}
            className="relative p-2 hover:bg-primary-50 rounded-full transition-colors"
          >
            <ShoppingCart size={24} className="text-namke-black/80" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-namke-terracotta text-namke-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </button>

          {/* Auth section */}
          {isAuthenticated ? (
            <div className="relative" ref={userMenuRef}>
              <button
                className="w-10 h-10 p-2 border-2 border-namke-darkGreen/20 grid items-center justify-center rounded-full hover:border-namke-olive transition-colors"
                onClick={() => setShowUserMenu((v) => !v)}
              >
                <User size={16} className="text-namke-black/80" />
              </button>
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-44 bg-namke-white border border-namke-darkGreen/15 rounded-2xl shadow-lg z-[10003]">
                  <button
                    onClick={() => { setShowUserMenu(false); navigate('/profile'); }}
                    className="w-full text-left px-4 py-3 hover:bg-primary-50 text-namke-black/80 text-sm rounded-t-2xl"
                  >
                    <i className="fa-regular fa-user mr-2"></i> {t('nav.profile')}
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-3 hover:bg-primary-50 text-namke-black/80 text-sm rounded-b-2xl"
                  >
                    <i className="fa-solid fa-arrow-right-from-bracket mr-2"></i> {t('nav.logout')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <Link to="/login">
                <button className="px-3 py-2 text-namke-black hover:bg-primary-50 rounded-full font-semibold text-sm transition-colors">
                  {t('nav.login')}
                </button>
              </Link>
              <Link to="/signup">
                <button className="px-4 py-2 bg-namke-darkGreen text-namke-white hover:bg-namke-black rounded-full font-semibold text-sm transition-colors">
                  {t('nav.signup')}
                </button>
              </Link>
            </div>
          )}
          </div>
        </div>
      </div>
      {/* Sidebar Component — rendue en dehors du conteneur fixe du header pour ne pas hériter de ses contraintes de hauteur/positionnement */}
      <Sidebar isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} onCartClick={() => setIsCartOpen(true)} />
      <CartSidebar isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
      {showAddressPopup && (
        <AddressPopup
          onSelect={handleAddressSelect}
          onClose={() => setShowAddressPopup(false)}
        />
      )}
    </>
    );
}